import { useEffect, useState } from "react";
import { FaArchive, FaTags, FaPlus, FaGlobe, FaExternalLinkAlt, FaSlidersH } from "react-icons/fa";
import { TAGS_CONSTANTS } from "../_MODULES/_API";
import { PageHeader } from "../../../../../_CORE/APP/Templates/_UI/PageHeader/_index";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { EmptyState, Skeleton } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { FormGrid, FieldInput, FieldColor, FieldDate, useForm } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { ViewLayout } from "../../../../_UI/ViewLayout";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { onApiAction } from "../../../../_UI/onApiAction";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { AdvancedFilterPanel, onDateMatches } from "../../../../_UI/AdvancedFilterPanel";
import { usePagedList } from "../../../../_UI/usePagedList";
import { API_CORE } from "../../../../../_CORE/API/ApiCORE";
import { isDuplicateRecord } from "../../../../_UI/recordUniqueness";
import "../_UI/_Styles/Tags.css";

export const TagsViewMain = () => {

    const [useSearch, setSearch] = useState("");
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [advanced, setAdvanced] = useState({ name: "", code: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });
    const [useIsModalOpen, setIsModalOpen] = useState(false);
    const [useGlobalTags, setGlobalTags] = useState([]);
    const [useGlobalName, setGlobalName] = useState("");
    const [useGlobalTagDetail, setGlobalTagDetail] = useState(null);
    const [useGlobalEntities, setGlobalEntities] = useState([]);

    const { apiGet: onGetTags, useApiData: TAGS, useApiLoading: IS_LOADING, mutateLocal } = useApiQuery({
        minTimeQuery: 250, enablePagination: false, startLoading: true,
    });

    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 300,
    });

    const onLoadTags = () => onGetTags({ endpoint: TAGS_CONSTANTS.LIST });

    const onLoadGlobalTags = async () => {
        const response = await API_CORE.SERVICES.GET_RESOURCES("/control/global-tags", {});
        const rows = response?.data?.data ?? response?.data ?? response;
        setGlobalTags(Array.isArray(rows) ? rows : []);
    };

    useEffect(() => { onLoadTags(); onLoadGlobalTags(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const FORM = useForm({
        initialValues: { name: "", code: "", color: "#e83a52" },

        schema: { name: { required: true, minLength: 2 } },

        onSubmit: async (values) => {

            if (isDuplicateRecord({ rows: Array.isArray(TAGS) ? TAGS : [], value: values.name })) {
                FORM.setError("name", "Ya existe una etiqueta con ese nombre", { focus: true });
                return;
            }

            const RESPONSE = await actionMutate({
                method: "POST", endpoint: TAGS_CONSTANTS.CREATE, successMessage: "Etiqueta creada",
                data: {
                    name: values.name.trim().toLowerCase(),
                    code: values.code ? values.code.trim().toUpperCase() : undefined,
                    color: values.color,
                },
            });

            if (!RESPONSE.success) { FORM.applyApiError(RESPONSE); return; }

            mutateLocal({ insert: RESPONSE.data });

            FORM.reset();
            setIsModalOpen(false);
        },
    });

    const onArchive = async (tag) => {

        const IS_CONFIRMED = await confirm.danger("Archivar etiqueta",
            `Se archiva "${tag.name}" y deja de aparecer en los registros activos. Podés restaurarla desde Archivados.`);

        if (!IS_CONFIRMED) return;

        mutateLocal({ id: tag.id, remove: true });
        const RESPONSE = await onApiAction({ method: "PATCH", endpoint: TAGS_CONSTANTS.ARCHIVE_ONE, pathParams: { id: tag.id }, successMessage: "Etiqueta archivada" });
        if (!RESPONSE.success) mutateLocal({ insert: tag });
    };

    const onCreateGlobalTag = async () => {
        if (!useGlobalName.trim()) return;
        if (isDuplicateRecord({ rows: useGlobalTags, value: useGlobalName })) return;
        await API_CORE.SERVICES.POST_RESOURCE("/control/global-tags", { name: useGlobalName.trim() });
        setGlobalName("");
        await onLoadGlobalTags();
    };

    const onOpenGlobalTag = async (tag) => {
        setGlobalTagDetail(tag);
        const response = await API_CORE.SERVICES.GET_RESOURCES(`/control/global-tags/${tag.id}/entities`, {});
        const rows = response?.data?.data ?? response?.data ?? response;
        setGlobalEntities(Array.isArray(rows) ? rows : []);
    };

    const ITEMS = TAGS ?? [];

    const VISIBLE = ITEMS
        .filter((tag) => !String(advanced.name || "").trim() || onMatchesSearch(advanced.name, [tag.name]))
        .filter((tag) => !String(advanced.code || "").trim() || onMatchesSearch(advanced.code, [tag.code]))
        .filter((tag) => onDateMatches(advanced.dateField === "CREATED" ? tag.createdAt : tag.updatedAt, advanced.dateRange, advanced.dateExact))
        .filter((tag) => onMatchesSearch(useSearch, [tag.name, tag.code]));
    const advancedCount = [String(advanced.name || "").trim(), String(advanced.code || "").trim(), advanced.dateRange?.[0] || advanced.dateRange?.[1], advanced.dateExact].filter(Boolean).length;
    const clearAdvanced = () => setAdvanced({ name: "", code: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });

    const PAGER = usePagedList(VISIBLE, 6, "page");

    return (
        <ViewLayout
            header={
                <PageHeader
                title="Etiquetas"
                description="Etiquetas globales para cruzar módulos, más las etiquetas técnicas de bitácora y scripts."
                icon={<FaTags />}
                    actions={<AppButton icon={<FaPlus />} onClick={() => setIsModalOpen(true)}>Nueva etiqueta</AppButton>} />
            }
            toolbar={
                ITEMS.length > 0 && (
                    <ViewToolbar
                        filters={<>
                            <SearchFilter value={useSearch} onChange={setSearch} total={ITEMS.length} matches={VISIBLE.length}
                                placeholder="Nombre o código de la etiqueta" />
                            <AppButton variant={advancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />} onClick={() => setAdvancedOpen((current) => !current)}>{advancedCount ? `Avanzado (${advancedCount})` : "Avanzado"}</AppButton>
                        </>}
                        expandedContent={<AdvancedFilterPanel isOpen={advancedOpen} activeCount={advancedCount} onClear={clearAdvanced}>
                            <FieldInput id="tags_adv_name" label="Nombre" value={advanced.name} onChange={(event) => setAdvanced((current) => ({ ...current, name: event.target.value }))} />
                            <FieldInput id="tags_adv_code" label="Código" value={advanced.code} onChange={(event) => setAdvanced((current) => ({ ...current, code: event.target.value }))} />
                            <FieldDate id="tags_adv_from" label="Desde" value={advanced.dateRange?.[0] || null} onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [date || null, current.dateRange?.[1] || null], dateExact: null }))} isClearable />
                            <FieldDate id="tags_adv_to" label="Hasta" value={advanced.dateRange?.[1] || null} onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [current.dateRange?.[0] || null, date || null], dateExact: null }))} isClearable />
                            <FieldDate id="tags_adv_exact" label="Fecha exacta" value={advanced.dateExact} onChange={(date) => setAdvanced((current) => ({ ...current, dateExact: date || null, dateRange: date ? [null, null] : current.dateRange }))} isClearable />
                        </AdvancedFilterPanel>}
                    total={PAGER.total}
                        perPage={PAGER.usePerPage}
                        onChangePerPage={PAGER.onChangePerPage}
                        exportColumns={[{ key: "name", label: "Etiqueta" }, { key: "code", label: "Código" }]}
                        exportRows={VISIBLE.map((tag) => ({ name: tag.name, code: tag.code }))}
                        exportName="etiquetas" />
                )
            } >

            <section className="LStudio__app_global_tags">
                <div className="LStudio__app_global_tags_head"><div><FaGlobe /><span><strong>Etiquetas globales</strong><small>Una etiqueta puede unir proyecto, guía, credencial, PDF, Trello, snippet y cualquier registro compatible.</small></span></div><div><input value={useGlobalName} onChange={(event) => setGlobalName(event.target.value)} placeholder="Nueva etiqueta global" /><AppButton size="sm" icon={<FaPlus />} onClick={onCreateGlobalTag}>Crear</AppButton></div></div>
                <div className="LStudio__app_global_tags_grid">{useGlobalTags.map((tag) => <button type="button" key={tag.id} onClick={() => onOpenGlobalTag(tag)}><i style={{ background: tag.color || "#777" }} /><span><strong>#{tag.code || tag.name}</strong><small>{tag.usageCount || 0} registros</small></span></button>)}</div>
            </section>

            <div className="LStudio__app_tags_legacy_title"><strong>Etiquetas técnicas</strong><small>Compatibilidad con Bitácora y Scripts.</small></div>

            {IS_LOADING && <Skeleton variant="box" height={120} />}

            {
                !IS_LOADING && VISIBLE.length === 0 && (
                    <EmptyState icon={<FaTags />} title="Sin etiquetas" description="Creá la primera para empezar a clasificar." />
                )
            }

            {
                !IS_LOADING && VISIBLE.length > 0 && (
                    <ul className="LStudio__app_tags_list">
                        {
                            PAGER.paged.map((tag) => (
                                <li key={tag.id} className="LStudio__app_tags_item">

                                    <span className="LStudio__app_tags_item_dot" style={{ background: tag.color || "#64748b" }} />

                                    <span className="LStudio__app_tags_item_name">{tag.name}</span>

                                    <code className="LStudio__app_tags_item_code">{tag.code}</code>

                                    <AppButton size="sm" variant="ghost" tone="danger" icon={<FaArchive />}
                                        isIconOnly aria-label="Archivar etiqueta" onClick={() => onArchive(tag)} />

                                </li>
                            ))
                        }
                    </ul>
                )
            }

            <ViewPagination page={PAGER.usePage} totalPages={PAGER.totalPages}
                total={PAGER.total} perPage={PAGER.usePerPage} onChange={PAGER.onChangePage} />

            <AppModal isOpen={useIsModalOpen} onClose={() => setIsModalOpen(false)}
                isDirty={FORM.isDirty} size="sm"
                title="Nueva etiqueta"
                footer={
                    <>
                        <AppButton variant="ghost" tone="neutral" onClick={() => setIsModalOpen(false)}>Cancelar</AppButton>
                        <AppButton onClick={FORM.onSubmit} isLoading={useActionApiIsLoading}>Guardar</AppButton>
                    </>
                } >

                <FormGrid columns={1}>

                    <FieldInput {...FORM.field("name")} label="Nombre" placeholder="backend" />

                    <FieldInput {...FORM.field("code")} label="Código" placeholder="BACKEND" hint="Si lo dejás vacío se genera solo" />

                    <FieldColor {...FORM.field("color")} label="Color" />

                </FormGrid>

            </AppModal>

            <AppModal isOpen={!!useGlobalTagDetail} onClose={() => { setGlobalTagDetail(null); setGlobalEntities([]); }}
                title={useGlobalTagDetail ? `#${useGlobalTagDetail.code || useGlobalTagDetail.name}` : "Etiqueta global"}
                description="Registros conectados a esta etiqueta en todo NOTEBOOK." size="lg"
                footer={<AppButton onClick={() => setGlobalTagDetail(null)}>Cerrar</AppButton>}>
                <div className="LStudio__app_global_tag_entities">
                    {useGlobalEntities.map((item) => <a key={`${item.type}-${item.id}`} href={item.url || "#"}><span><strong>{item.title}</strong><small>{item.type}</small></span><FaExternalLinkAlt /></a>)}
                    {!useGlobalEntities.length && <EmptyState icon={<FaTags />} title="Sin registros asociados" />}
                </div>
            </AppModal>
        </ViewLayout>
    );
};
